import {
  ApiLocation,
  ApiLocationItem,
  ApiPageType,
  ApiProjectDefault,
} from "@incendium/api";
import {
  ChevronRight,
  Delete,
  Edit,
  Link as LinkIcon,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
  styled,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  LocationPageTypesField,
  LocationRules,
  createPageType,
} from "features/location";
import { formatEnumVal } from "Helpers/enumToText";
import { useSelectedProject } from "Hooks";
import { useIsMobile } from "Hooks/useIsMobile";
import { usePageTypes } from "Hooks/usePageTypes";
import produce from "immer";
import { useCallback, useEffect, useState } from "react";

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  boxShadow: "none",
  border: "none",
  borderBottom: `1px solid ${theme.palette.grey[200]}`,
  "&:before": { display: "none" },
  "&.Mui-expanded": { margin: 0 },
  "&:last-of-type": {
    borderBottom: "none",
  },
}));

const PageTypeChip = styled(Chip)(({ theme }) => ({
  backgroundColor: alpha(
    theme.palette.primary.vibrant || theme.palette.primary.main,
    0.12,
  ),
  color: theme.palette.primary.main,
  fontWeight: 500,
}));

interface ILocationAccordionItemProps {
  location: ApiLocation;
  onEdit?: (location: ApiLocation) => void;
  onDelete?: (location: ApiLocation) => void;
  onSave?: (location: ApiLocation) => Promise<void>;
  showDelete?: boolean;
}

function LocationAccordionItem({
  location: initialLocation,
  onEdit,
  onDelete,
  onSave,
  showDelete = true,
}: ILocationAccordionItemProps) {
  const [editMode, setEditMode] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [location, setLocation] = useState(initialLocation);
  const [saving, setSaving] = useState(false);
  const { selectedProject } = useSelectedProject();
  const { pageTypes, setPageTypes } = usePageTypes();
  const isMobile = useIsMobile();

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onEdit) {
      onEdit(location);
    } else {
      setEditMode(true);
      setExpanded(true);
    }
  };

  const handleSave = async () => {
    if (!onSave) {
      return;
    }
    setSaving(true);
    try {
      await onSave(location);
      setEditMode(false);
    } catch {
      // caller handles notifications
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setLocation(initialLocation);
    setEditMode(false);
    setExpanded(false);
  };

  const updateRules = (rules: ApiLocationItem[]) => {
    setLocation(
      produce(location, (draft) => {
        draft.items = rules;
      }),
    );
  };

  const updateName = (name: string) => {
    setLocation(
      produce(location, (draft) => {
        draft.name = name;
      }),
    );
  };

  const updateUrlMatch = (urlMatch: string) => {
    setLocation(
      produce(location, (draft) => {
        draft.urlMatch = urlMatch;
      }),
    );
  };

  const updatePageTypes = (pageTypes: ApiPageType[]) => {
    setLocation(
      produce(location, (draft) => {
        draft.pageTypes = pageTypes;
      }),
    );
  };

  const handleCreatePageType = useCallback(
    async (name: string): Promise<ApiPageType> => {
      if (!selectedProject?.id) {
        throw new Error("No project selected");
      }

      const newPageType = await createPageType(selectedProject.id, name);

      setPageTypes(
        produce(pageTypes, (draft) => {
          draft.push(newPageType);
        }),
      );

      return newPageType;
    },
    [selectedProject, pageTypes, setPageTypes],
  );

  const isDisabled =
    location._default &&
    ![
      ApiProjectDefault.UNSPECIFIED,
      ApiProjectDefault.ONBOARDING_CREATED,
    ].includes(location._default);

  useEffect(() => {
    setLocation(initialLocation);
  }, [initialLocation]);

  return (
    <StyledAccordion
      disableGutters
      expanded={expanded}
      onChange={(_, isExpanded) => {
        setExpanded(isExpanded);
        if (editMode && !isExpanded) {
          handleCancel();
        }
      }}
    >
      <AccordionSummary
        expandIcon={
          <ChevronRight
            sx={{
              color: "text.secondary",
              transition: "transform 0.2s",
              transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
            }}
          />
        }
        sx={{
          px: 2,
          minHeight: 72,
          "& .MuiAccordionSummary-content": { my: 1.5, mr: 1 },
          "&.Mui-focusVisible": { backgroundColor: "transparent !important" },
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          alignItems={{ xs: "flex-start", md: "center" }}
          sx={{
            width: "100%",
            pr: 1,
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          <Stack sx={{ flex: 1, minWidth: 0 }} spacing={0.5}>
            <Typography variant="subtitle2" fontWeight={600}>
              {location.name}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              noWrap={!isMobile}
            >
              {location.urlMatch}
            </Typography>
          </Stack>

          <Stack
            direction="row"
            alignItems="center"
            spacing={0.75}
            flexShrink={0}
            onClick={(e) => e.stopPropagation()}
            sx={{ display: { xs: "none", md: "flex" } }}
          >
            {!editMode &&
              (location.pageTypes || []).map((pageType, idx) => (
                <PageTypeChip key={idx} label={pageType.name} size="small" />
              ))}
            {!editMode && (
              <IconButton
                size="small"
                color="primary"
                onClick={handleEditClick}
                disabled={isDisabled}
              >
                <Edit fontSize="small" />
              </IconButton>
            )}
            {!editMode && showDelete && (
              <IconButton
                size="small"
                color="secondary"
                onClick={() => onDelete?.(location)}
                disabled={isDisabled}
              >
                <Delete fontSize="small" />
              </IconButton>
            )}
          </Stack>
        </Stack>
      </AccordionSummary>

      <AccordionDetails
        sx={{
          px: 2,
          pt: editMode ? 2 : 0,
          pb: 2,
          borderTop: editMode ? 1 : 0,
          borderColor: "grey.200",
        }}
      >
        {editMode ? (
          <Stack spacing={3}>
            <TextField
              size="small"
              label="Location name"
              value={location.name}
              onChange={(e) => updateName(e.target.value)}
              variant="outlined"
              fullWidth
            />
            <TextField
              size="small"
              label="URL match pattern"
              value={location.urlMatch}
              onChange={(e) => updateUrlMatch(e.target.value)}
              variant="outlined"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LinkIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                  </InputAdornment>
                ),
              }}
            />
            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Page types
              </Typography>
              <LocationPageTypesField
                value={location.pageTypes || []}
                onChange={updatePageTypes}
                onCreateNew={handleCreatePageType}
                size="small"
              />
            </Box>
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
                display="block"
                sx={{ mb: 1.5, letterSpacing: 0.5 }}
              >
                MATCHING RULES
              </Typography>
              <LocationRules
                rules={location.items || []}
                onChange={updateRules}
                location={location}
                size="small"
              />
            </Box>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="flex-end"
              sx={{ pt: 1 }}
            >
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? "Saving..." : "Save Changes"}
              </Button>
            </Stack>
          </Stack>
        ) : (
          <>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
              display="block"
              mb={1.5}
              sx={{ letterSpacing: 0.5 }}
            >
              MATCHING RULES
            </Typography>
            <Stack spacing={1}>
              {(location.items || []).length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontStyle="italic"
                >
                  No rules defined
                </Typography>
              ) : (
                (location.items || []).map((item, idx) => (
                  <Paper
                    key={idx}
                    variant="outlined"
                    sx={{ p: 1.5, bgcolor: "grey.50" }}
                  >
                    <Stack direction="row" spacing={2} alignItems="center">
                      {idx > 0 && (
                        <Typography
                          variant="caption"
                          color="primary"
                          fontWeight={700}
                        >
                          AND
                        </Typography>
                      )}
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        flex={1}
                      >
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          color="primary"
                        >
                          {formatEnumVal(item.condition || "")}
                        </Typography>
                        <Typography variant="body2">{item.value}</Typography>
                      </Stack>
                    </Stack>
                  </Paper>
                ))
              )}
            </Stack>
          </>
        )}
      </AccordionDetails>
    </StyledAccordion>
  );
}

export default LocationAccordionItem;
